<template>
  <section class="community-page">
    <div class="community-shell">
      <header class="community-hero">
        <div class="hero-copy">
          <span class="eyebrow">WINTOAI Q&amp;A</span>
          <h1>我要提问</h1>
          <p>遇到使用问题时，请在这里详细描述并上传相关截图，其他用户或平台技术人员会协助解答。</p>
        </div>
        <div class="hero-actions">
          <button class="primary-button" type="button" @click="openDialog">
            <i class="el-icon-edit"></i><span>我要提问</span>
          </button>
          <button class="secondary-hero-button" type="button" @click="contactCustomerService">
            <i class="el-icon-service"></i><span>联系客服</span>
          </button>
        </div>
      </header>

      <div class="community-toolbar">
        <div>
          <strong>全部问题</strong>
          <span>{{ totalElements }} 个问题，按最新提问排序</span>
        </div>
        <button class="refresh-button" type="button" :disabled="loading || deletingId !== null" @click="loadFeedback(0)">
          <i :class="loading ? 'el-icon-loading' : 'el-icon-refresh'"></i> 刷新
        </button>
      </div>

      <div v-if="loading && !items.length" class="community-state">
        <i class="el-icon-loading"></i><strong>正在加载问题</strong>
      </div>
      <div v-else-if="error && !items.length" class="community-state state-error" role="alert">
        <i class="el-icon-warning-outline"></i><strong>{{ error }}</strong>
        <button type="button" @click="loadFeedback(0)">重新加载</button>
      </div>
      <div v-else-if="!items.length" class="community-state">
        <i class="el-icon-chat-line-round"></i><strong>目前还没有问题</strong>
        <span>遇到疑问时，可以提交第一个问题。</span>
        <button class="state-action" type="button" @click="openDialog">我要提问</button>
      </div>
      <div v-else class="post-list">
        <article
          v-for="item in items"
          :key="item.id"
          class="post-card"
        >
          <header class="post-author">
            <span class="avatar">{{ submitterInitial(item.submitter) }}</span>
            <div class="author-copy">
              <strong>{{ item.submitter.displayName || item.submitter.username }}</strong>
              <span>@{{ item.submitter.username }} · {{ formatTime(item.createdAt) }}</span>
            </div>
            <div class="post-actions">
              <span class="post-status"><i></i> 已提问</span>
              <button
                v-if="isOwnFeedback(item)"
                class="post-delete-button"
                type="button"
                :disabled="loading || deletingId === item.id"
                :aria-label="`删除问题 #${item.id}`"
                @click="confirmDelete(item)"
              >
                <i :class="deletingId === item.id ? 'el-icon-loading' : 'el-icon-delete'"></i>
                {{ deletingId === item.id ? '删除中' : '删除' }}
              </button>
            </div>
          </header>
          <div class="post-summary">
            <p class="post-summary-text">{{ summaryText(item.content) }}</p>
            <div v-if="item.imageUrls && item.imageUrls.length" class="feed-image-grid" :class="`image-count-${Math.min(item.imageUrls.length, 4)}`">
              <button v-for="(url, imageIndex) in item.imageUrls.slice(0, 9)" :key="url" type="button" :aria-label="`查看第 ${imageIndex + 1} 张图片`" @click="previewPostImages(item.imageUrls, imageIndex)">
                <img :src="url" :alt="`${item.submitter.displayName || item.submitter.username} 上传的第 ${imageIndex + 1} 张问题截图`" loading="lazy" />
                <span v-if="imageIndex === 8 && item.imageUrls.length > 9">+{{ item.imageUrls.length - 9 }}</span>
                <i class="el-icon-zoom-in"></i>
              </button>
            </div>
            <button v-if="item.latestComment" class="latest-reply" type="button" @click="toggleReplies(item)">
              <span class="comment-avatar">{{ submitterInitial(item.latestComment.submitter) }}</span>
              <span class="latest-reply-copy">
                <span class="latest-reply-head">
                  <strong>{{ item.latestComment.submitter.displayName || item.latestComment.submitter.username }}</strong>
                  <small>最新回答 · {{ formatTime(item.latestComment.createdAt) }}</small>
                </span>
                <span class="latest-reply-content">{{ summaryText(item.latestComment.content) }}</span>
              </span>
              <i class="el-icon-arrow-right"></i>
            </button>
            <div class="summary-meta">
              <button class="reply-toggle" type="button" :aria-expanded="isRepliesExpanded(item.id) ? 'true' : 'false'" @click="toggleReplies(item)"><i class="el-icon-chat-line-round"></i>{{ item.commentCount || 0 }} 条回答</button>
              <span v-if="item.imageUrls && item.imageUrls.length"><i class="el-icon-picture-outline"></i>{{ item.imageUrls.length }} 张截图</span>
              <button class="detail-hint" type="button" :aria-label="`查看问题 #${item.id} 详情`" @click="openDetail(item)">
                查看详情 <i class="el-icon-arrow-right"></i>
              </button>
            </div>
            <section v-if="isRepliesExpanded(item.id)" class="inline-discussion" :aria-label="`${item.title || '问题'}的回答`">
              <div v-if="commentLoadingByPost[item.id]" class="inline-comment-state"><i class="el-icon-loading"></i> 正在加载回答</div>
              <template v-else>
                <div v-if="commentsByPost[item.id] && commentsByPost[item.id].length" class="inline-comment-list">
                  <article v-for="comment in commentsByPost[item.id]" :key="comment.id" class="inline-comment">
                    <span class="comment-avatar">{{ submitterInitial(comment.submitter) }}</span>
                    <div><header><strong>{{ comment.submitter.displayName || comment.submitter.username }}</strong><small>{{ formatTime(comment.createdAt) }}</small></header><p>{{ comment.content }}</p></div>
                  </article>
                </div>
                <div v-else class="inline-comment-state">还没有回答，如果你知道解决方法，可以帮助提问者</div>
                <form class="inline-reply-form" @submit.prevent="submitInlineReply(item)">
                  <textarea v-model.trim="replyDrafts[item.id]" maxlength="1000" rows="2" placeholder="写下解决方法、操作步骤或你的建议…" :disabled="replySubmittingByPost[item.id]"></textarea>
                  <div><span>{{ (replyDrafts[item.id] || '').length }}/1000</span><button type="submit" :disabled="replySubmittingByPost[item.id] || !(replyDrafts[item.id] || '').trim()"><i :class="replySubmittingByPost[item.id] ? 'el-icon-loading' : 'el-icon-position'"></i>{{ replySubmittingByPost[item.id] ? '提交中' : '提交回答' }}</button></div>
                </form>
              </template>
            </section>
          </div>
        </article>
      </div>

      <AppPagination :page="currentPage + 1" :page-size="pageSize" :total="totalElements" :loading="loading" @change="handlePageChange" />
    </div>

    <el-dialog title="查看图片" :visible.sync="imageDialogVisible" width="min(920px, 92vw)" custom-class="community-image-dialog" append-to-body>
      <div class="community-image-viewer">
        <button v-if="previewImages.length > 1" class="image-nav image-nav-prev" type="button" aria-label="上一张" @click="showPreviousImage"><i class="el-icon-arrow-left"></i></button>
        <img class="preview-image" :src="previewImageUrl" :alt="`图片预览 ${previewIndex + 1}/${previewImages.length}`" />
        <button v-if="previewImages.length > 1" class="image-nav image-nav-next" type="button" aria-label="下一张" @click="showNextImage"><i class="el-icon-arrow-right"></i></button>
        <span v-if="previewImages.length > 1" class="image-counter">{{ previewIndex + 1 }} / {{ previewImages.length }}</span>
      </div>
    </el-dialog>

    <el-dialog
      title="我要提问"
      :visible.sync="dialogVisible"
      width="560px"
      custom-class="feedback-dialog"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <form class="dialog-form" @submit.prevent="submitFeedback">
        <p class="dialog-intro">请描述遇到的问题、操作过程和预期结果，信息越完整越容易获得准确回答。</p>
        <label class="dialog-label" for="feedback-content">问题描述 <em>必填</em><span>{{ content.length }}/2000</span></label>
        <textarea
          id="feedback-content"
          v-model="content"
          class="dialog-textarea"
          maxlength="2000"
          rows="6"
          placeholder="请说明你遇到了什么问题、进行了哪些操作，以及希望得到什么结果…"
          :aria-invalid="contentError ? 'true' : 'false'"
        ></textarea>
        <small v-if="contentError" class="dialog-error" role="alert">{{ contentError }}</small>

        <label class="dialog-label upload-label" for="feedback-images">添加问题截图 <span>可选，最多 5 张</span></label>
        <label
          class="upload-zone"
          :class="{ dragging, disabled: busy || attachments.length >= maxFiles }"
          for="feedback-images"
          @dragenter.prevent="dragging = true"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="handleDrop"
        >
          <input id="feedback-images" ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp" multiple :disabled="busy || attachments.length >= maxFiles" @change="handleFileInput" />
          <i class="el-icon-picture-outline"></i>
          <span>点击选择或拖拽图片到这里<small>支持 JPG、PNG、WebP，单张不超过 10MB</small></span>
        </label>
        <small v-if="imageError" class="dialog-error" role="alert">{{ imageError }}</small>
        <div v-if="attachments.length" class="attachment-grid">
          <figure v-for="attachment in attachments" :key="attachment.id">
            <img :src="attachment.previewUrl" :alt="attachment.file.name" />
            <button type="button" :aria-label="`移除 ${attachment.file.name}`" :disabled="busy" @click="removeAttachment(attachment.id)"><i class="el-icon-close"></i></button>
          </figure>
        </div>
        <div v-if="submitError" class="dialog-submit-error" role="alert"><i class="el-icon-warning-outline"></i>{{ submitError }}</div>
      </form>
      <span slot="footer" class="dialog-footer">
        <button class="secondary-button" type="button" :disabled="busy" @click="dialogVisible = false">取消</button>
        <button class="primary-button dialog-submit" type="button" :disabled="!canSubmit" @click="submitFeedback">
          <i v-if="busy" class="el-icon-loading"></i><i v-else class="el-icon-position"></i>
          {{ busy ? submitButtonText : '提交问题' }}
        </button>
      </span>
    </el-dialog>

  </section>
</template>

<script>
import { apiRequest } from '@/services/api'
import { authState } from '@/services/auth'

const MAX_FILES = 5
const MAX_FILE_BYTES = 10 * 1024 * 1024
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export default {
  name: 'FeedbackCenter',
  data() {
    return {
      content: '',
      contentError: '',
      imageError: '',
      attachments: [],
      dragging: false,
      uploading: false,
      submitting: false,
      uploadProgress: 0,
      submitError: '',
      items: [],
      loading: false,
      error: '',
      currentPage: 0,
      pageSize: 12,
      totalElements: 0,
      totalPages: 0,
      dialogVisible: false,
      deletingId: null,
      expandedPostIds: [],
      commentsByPost: {},
      commentLoadingByPost: {},
      replyDrafts: {},
      replySubmittingByPost: {},
      imageDialogVisible: false,
      previewImages: [],
      previewIndex: 0,
      requestControllers: []
    }
  },
  computed: {
    currentUser() { return authState.user },
    maxFiles() { return MAX_FILES },
    busy() { return this.uploading || this.submitting },
    canSubmit() { return !this.busy && Boolean(this.content.trim()) },
    previewImageUrl() { return this.previewImages[this.previewIndex] || '' },
    submitButtonText() {
      if (this.uploading) return `正在上传 ${this.uploadProgress}/${this.attachments.length}`
      if (this.submitting) return '正在提交'
      return '提交问题'
    }
  },
  created() {
    this.loadFeedback(0)
  },
  beforeDestroy() {
    this.releaseAttachments()
    this.requestControllers.forEach(controller => controller.abort())
  },
  methods: {
    openDialog() {
      this.dialogVisible = true
      this.$nextTick(() => document.getElementById('feedback-content') && document.getElementById('feedback-content').focus())
    },
    contactCustomerService() {
      this.$emit('open-customer-service')
    },
    resetForm() {
      this.content = ''
      this.contentError = ''
      this.imageError = ''
      this.submitError = ''
      this.releaseAttachments()
    },
    validateForm() {
      this.contentError = this.content.trim() ? '' : '请输入问题描述'
      return !this.contentError
    },
    handleFileInput(event) {
      this.addFiles(Array.from(event.target.files || []))
      event.target.value = ''
    },
    handleDrop(event) {
      this.dragging = false
      if (!this.busy) this.addFiles(Array.from(event.dataTransfer.files || []))
    },
    addFiles(files) {
      this.imageError = ''
      const available = MAX_FILES - this.attachments.length
      if (files.length > available) this.imageError = `最多上传 ${MAX_FILES} 张图片`
      files.slice(0, available).forEach(file => {
        if (!ACCEPTED_TYPES.has(file.type)) {
          this.imageError = '仅支持 JPG、PNG 或 WebP 图片'
          return
        }
        if (file.size >= MAX_FILE_BYTES) {
          this.imageError = `${file.name} 必须小于 10MB，请压缩后再上传`
          return
        }
        this.attachments.push({ id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, file, previewUrl: URL.createObjectURL(file) })
      })
    },
    removeAttachment(id) {
      const index = this.attachments.findIndex(item => item.id === id)
      if (index < 0) return
      URL.revokeObjectURL(this.attachments[index].previewUrl)
      this.attachments.splice(index, 1)
      this.imageError = ''
    },
    releaseAttachments() {
      this.attachments.forEach(item => URL.revokeObjectURL(item.previewUrl))
      this.attachments = []
    },
    async uploadAttachment(attachment) {
      const body = new FormData()
      body.append('file', attachment.file)
      return apiRequest('/api/images/upload', { method: 'POST', body, timeoutMs: 120000, timeoutMessage: '图片上传超时，请稍后重试' }, { controllers: this.requestControllers })
    },
    async submitFeedback() {
      if (!this.validateForm() || this.busy) return
      this.submitError = ''
      try {
        const imageUrls = []
        if (this.attachments.length) {
          this.uploading = true
          this.uploadProgress = 0
          for (const attachment of this.attachments) {
            const uploaded = await this.uploadAttachment(attachment)
            imageUrls.push(uploaded.url)
            this.uploadProgress += 1
          }
          this.uploading = false
        }
        this.submitting = true
        await apiRequest('/api/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=UTF-8' },
          body: JSON.stringify({ content: this.content.trim(), imageUrls }),
          timeoutMs: 30000,
          timeoutMessage: '问题提交超时，请稍后重试'
        }, { controllers: this.requestControllers })
        this.dialogVisible = false
        this.$message({ message: '问题已提交，等待其他用户或平台技术人员回答', type: 'success', showClose: true, duration: 2600, offset: 24 })
        await this.loadFeedback(0)
      } catch (requestError) {
        this.submitError = requestError.message || '问题提交失败'
      } finally {
        this.uploading = false
        this.submitting = false
      }
    },
    async loadFeedback(page) {
      if (this.loading) return
      this.loading = true
      this.error = ''
      try {
        const result = await apiRequest(`/api/feedback?scope=community&page=${page}&size=${this.pageSize}`, { timeoutMs: 20000, timeoutMessage: '问题列表加载超时，请稍后重试' }, { controllers: this.requestControllers })
        this.items = result.items || []
        this.expandedPostIds = []
        this.commentsByPost = {}
        this.commentLoadingByPost = {}
        this.replyDrafts = {}
        this.replySubmittingByPost = {}
        this.currentPage = Number(result.page || 0)
        this.totalElements = Number(result.totalElements || 0)
        this.totalPages = Number(result.totalPages || 0)
      } catch (requestError) {
        this.error = requestError.message || '问题列表加载失败'
      } finally {
        this.loading = false
      }
    },
    handlePageChange(page) {
      this.loadFeedback(Number(page) - 1)
    },
    isOwnFeedback(item) {
      if (!item || !item.submitter || !this.currentUser) return false
      if (typeof item.canDelete === 'boolean') return item.canDelete
      const sameId = item.submitter.id != null && this.currentUser.id != null &&
        String(item.submitter.id) === String(this.currentUser.id)
      const sameUsername = item.submitter.username && this.currentUser.username &&
        String(item.submitter.username).trim().toLowerCase() === String(this.currentUser.username).trim().toLowerCase()
      return Boolean(sameId || sameUsername)
    },
    async confirmDelete(item) {
      if (!this.isOwnFeedback(item) || this.loading || this.deletingId !== null) return
      try {
        await this.$confirm('删除后无法恢复，确定删除这个问题吗？', '删除问题', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          distinguishCancelAndClose: true,
          closeOnClickModal: false
        })
      } catch (action) {
        return
      }

      this.deletingId = item.id
      const targetPage = this.items.length === 1 && this.currentPage > 0
        ? this.currentPage - 1
        : this.currentPage
      try {
        await apiRequest(`/api/feedback/${item.id}`, {
          method: 'DELETE',
          timeoutMs: 20000,
          timeoutMessage: '问题删除超时，请稍后重试'
        }, { controllers: this.requestControllers })
        this.items = this.items.filter(feedback => String(feedback.id) !== String(item.id))
        this.totalElements = Math.max(0, this.totalElements - 1)
        this.$message({ message: '问题已删除', type: 'success', showClose: true, duration: 2200, offset: 24 })
        await this.loadFeedback(targetPage)
      } catch (requestError) {
        this.$message({
          message: requestError.message || '问题删除失败',
          type: 'error',
          showClose: true,
          duration: 3200,
          offset: 24
        })
      } finally {
        this.deletingId = null
      }
    },
    submitterInitial(submitter) {
      return String((submitter && (submitter.displayName || submitter.username)) || '用户').trim().slice(0, 1).toUpperCase()
    },
    summaryText(content) {
      const value = String(content || '').replace(/\s+/g, ' ').trim()
      return value.length > 240 ? `${value.slice(0, 240)}…` : value
    },
    isRepliesExpanded(id) {
      return this.expandedPostIds.includes(String(id))
    },
    async toggleReplies(item) {
      const key = String(item.id)
      if (this.isRepliesExpanded(key)) {
        this.expandedPostIds = this.expandedPostIds.filter(id => id !== key)
        return
      }
      this.expandedPostIds.push(key)
      if (!Object.prototype.hasOwnProperty.call(this.replyDrafts, item.id)) this.$set(this.replyDrafts, item.id, '')
      if (!Object.prototype.hasOwnProperty.call(this.commentsByPost, item.id)) await this.loadPostComments(item)
    },
    async loadPostComments(item) {
      this.$set(this.commentLoadingByPost, item.id, true)
      try {
        const result = await apiRequest(`/api/feedback/${encodeURIComponent(item.id)}`, { timeoutMs: 20000, timeoutMessage: '回答加载超时，请稍后重试' }, { controllers: this.requestControllers })
        this.$set(this.commentsByPost, item.id, result.comments || [])
      } catch (requestError) {
        this.$message({ message: requestError.message || '回答加载失败', type: 'error', showClose: true, duration: 3000, offset: 24 })
        this.$set(this.commentsByPost, item.id, [])
      } finally {
        this.$set(this.commentLoadingByPost, item.id, false)
      }
    },
    async submitInlineReply(item) {
      const content = String(this.replyDrafts[item.id] || '').trim()
      if (!content || this.replySubmittingByPost[item.id]) return
      this.$set(this.replySubmittingByPost, item.id, true)
      try {
        const comment = await apiRequest(`/api/feedback/${encodeURIComponent(item.id)}/comments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=UTF-8' },
          body: JSON.stringify({ content }),
          timeoutMs: 20000,
          timeoutMessage: '回答提交超时，请稍后重试'
        }, { controllers: this.requestControllers })
        const comments = this.commentsByPost[item.id] || []
        this.$set(this.commentsByPost, item.id, [...comments, comment])
        this.$set(this.replyDrafts, item.id, '')
        this.$set(item, 'commentCount', Number(item.commentCount || 0) + 1)
        this.$set(item, 'latestComment', comment)
        this.$message({ message: '回答已提交', type: 'success', showClose: true, duration: 2000, offset: 24 })
      } catch (requestError) {
        this.$message({ message: requestError.message || '回答提交失败', type: 'error', showClose: true, duration: 3000, offset: 24 })
      } finally {
        this.$set(this.replySubmittingByPost, item.id, false)
      }
    },
    previewPostImages(images, index) {
      this.previewImages = Array.isArray(images) ? images.filter(Boolean) : []
      this.previewIndex = Math.min(Math.max(0, index), Math.max(0, this.previewImages.length - 1))
      this.imageDialogVisible = Boolean(this.previewImages.length)
    },
    showPreviousImage() {
      if (!this.previewImages.length) return
      this.previewIndex = (this.previewIndex - 1 + this.previewImages.length) % this.previewImages.length
    },
    showNextImage() {
      if (!this.previewImages.length) return
      this.previewIndex = (this.previewIndex + 1) % this.previewImages.length
    },
    openDetail(item) {
      this.$router.push({ name: 'feedbackDetail', params: { id: item.id } })
    },
    formatTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value).replace('T', ' ').slice(0, 16)
      const pad = valuePart => String(valuePart).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.community-page { width: calc(100% - 68px); margin: 24px auto 56px; color: #26322b; }
.community-shell { width: 100%; margin: 0; }
button { cursor: pointer; }
button:focus-visible, input:focus-visible, textarea:focus-visible { outline: 3px solid rgba(37,99,235,.2); outline-offset: 2px; }
.community-hero { display: flex; align-items: center; justify-content: space-between; gap: 28px; margin-bottom: 18px; border: 1px solid #e0e6e2; border-radius: 7px; padding: 26px 30px; background: linear-gradient(120deg, #fff 0%, #f7faff 72%, #eef4ff 100%); box-shadow: 0 8px 26px rgba(29,43,35,.045); }
.hero-copy { min-width: 0; }
.eyebrow { color: #3769ce; font-size: 10px; font-weight: 800; letter-spacing: 1.7px; }
.hero-copy h1 { margin: 7px 0 6px; color: #26352c; font-size: 24px; letter-spacing: -.5px; }
.hero-copy p { margin: 0; color: #7b877f; font-size: 12px; line-height: 1.65; }
.hero-actions { display: flex; align-items: center; gap: 9px; flex: 0 0 auto; }
.secondary-hero-button { min-height: 40px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1px solid #d7e0db; border-radius: 6px; padding: 0 16px; background: #fff; color: #53645a; font-size: 12px; font-weight: 700; transition: border-color .18s ease, color .18s ease, background .18s ease; }
.secondary-hero-button:hover { border-color: #9bb4e5; background: #f7faff; color: #315fb9; }
.primary-button { min-height: 40px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 0; border-radius: 6px; padding: 0 18px; background: #2563eb; color: #fff; font-size: 12px; font-weight: 700; box-shadow: 0 7px 16px rgba(37,99,235,.2); transition: background .18s ease, transform .18s ease; }
.primary-button:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); }
.primary-button:disabled, .secondary-button:disabled { cursor: not-allowed; opacity: .55; }
.community-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 10px; padding: 0 2px; }
.community-toolbar > div { display: flex; align-items: baseline; gap: 10px; }
.community-toolbar strong { color: #33433a; font-size: 15px; }
.community-toolbar span { color: #929d96; font-size: 11px; }
.refresh-button { min-height: 32px; border: 1px solid #dce3de; border-radius: 5px; padding: 0 11px; background: #fff; color: #647169; font-size: 11px; transition: border-color .18s ease, color .18s ease; }
.refresh-button:hover:not(:disabled) { border-color: #9db5e5; color: #315fb9; }
.post-list { display: grid; gap: 10px; }
.post-card { border: 1px solid #e2e8e4; border-radius: 6px; padding: 17px 18px 14px; background: #fff; text-align: left; transition: border-color .18s ease, box-shadow .18s ease; }
.post-card:hover { border-color: #d2dce8; box-shadow: 0 6px 18px rgba(46,70,56,.05); }
.post-author { display: flex; align-items: center; gap: 10px; }
.avatar { width: 38px; height: 38px; display: grid; place-items: center; flex: 0 0 38px; border-radius: 50%; background: #e9efff; color: #3261c0; font-size: 15px; font-weight: 800; }
.author-copy { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.author-copy strong { overflow: hidden; color: #3b65a9; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.author-copy span { color: #a0aaa4; font-size: 10px; }
.post-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; }
.post-status { color: #4c9260; font-size: 10px; white-space: nowrap; }
.post-status i { width: 6px; height: 6px; display: inline-block; margin-right: 4px; border-radius: 50%; background: #48a764; }
.post-delete-button, .detail-delete-button { min-height: 32px; display: inline-flex; align-items: center; justify-content: center; gap: 5px; border: 1px solid #ebcbc8; border-radius: 5px; padding: 0 10px; background: #fffafa; color: #a83b35; font-size: 11px; font-weight: 700; transition: border-color .18s ease, background .18s ease, color .18s ease; }
.post-delete-button:hover:not(:disabled), .detail-delete-button:hover:not(:disabled) { border-color: #d99893; background: #fff3f2; color: #8f2f2a; }
.post-delete-button:disabled, .detail-delete-button:disabled { cursor: wait; opacity: .62; }
.post-summary { margin: 13px 0 0 48px; }
.post-summary-text { margin: 0 0 11px; color: #46534b; font-size: 14px; line-height: 1.7; white-space: pre-wrap; word-break: break-word; }
.feed-image-grid { width:min(580px,100%);display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin:10px 0 12px; }
.feed-image-grid.image-count-1 { width:min(420px,100%);grid-template-columns:1fr; }.feed-image-grid.image-count-2 { width:min(520px,100%);grid-template-columns:repeat(2,1fr); }
.feed-image-grid button { aspect-ratio:4/3;overflow:hidden;position:relative;border:0;border-radius:5px;padding:0;background:#eef1ef;cursor:zoom-in; }
.feed-image-grid.image-count-1 button { aspect-ratio:16/10; }.feed-image-grid img { width:100%;height:100%;display:block;object-fit:cover;transition:transform .2s ease,filter .2s ease; }
.feed-image-grid button>i { position:absolute;right:8px;bottom:8px;display:grid;place-items:center;border-radius:50%;padding:6px;background:rgba(25,35,29,.66);color:#fff;font-size:13px;opacity:0;transition:opacity .18s ease; }
.feed-image-grid button>span { position:absolute;inset:0;display:grid;place-items:center;background:rgba(18,29,22,.56);color:#fff;font-size:20px;font-weight:700; }
.feed-image-grid button:hover img { filter:brightness(.88);transform:scale(1.025); }.feed-image-grid button:hover>i,.feed-image-grid button:focus-visible>i { opacity:1; }
.latest-reply { width: min(680px, 100%); display: flex; align-items: flex-start; gap: 9px; margin: 10px 0 12px; border: 1px solid #e2e7e4; border-radius: 5px; padding: 10px 11px; background: #f8faf9; color: inherit; text-align: left; cursor: pointer; transition: border-color .18s ease, background .18s ease; }
.latest-reply:hover, .latest-reply:focus-visible { border-color: #c7d4e8; background: #f5f8fd; outline: 0; }
.latest-reply-copy { min-width: 0; display: flex; flex: 1; flex-direction: column; gap: 4px; }
.latest-reply-head { display: flex; align-items: baseline; gap: 8px; }
.latest-reply-head strong { color: #3f5f92; font-size: 11px; }
.latest-reply-head small { color: #98a39d; font-size: 9px; }
.latest-reply-content { overflow: hidden; color: #4a564f; font-size: 11px; line-height: 1.6; text-overflow: ellipsis; white-space: nowrap; }
.latest-reply > i { align-self: center; color: #a0aaa4; font-size: 11px; }
.summary-meta { display: flex; align-items: center; gap: 16px; color: #9aa49e; font-size: 10px; }
.summary-meta i { margin-right: 4px; color: #84918a; }
.reply-toggle { min-height:32px;display:inline-flex;align-items:center;border:0;padding:0 4px;background:transparent;color:#748078;font-size:10px;cursor:pointer; }.reply-toggle:hover { color:#315fae; }
.detail-hint { min-height: 32px; display: inline-flex; align-items: center; margin-left: auto; border: 0; border-radius: 4px; padding: 0 4px; background: transparent; color: #4d6fab; font-size: 11px; font-weight: 700; }
.detail-hint:hover { background: #f4f7fc; color: #315b9f; }
.detail-hint i { margin: 0 0 0 3px; color: inherit; }
.inline-discussion { margin-top:10px;border-top:1px solid #e4e9e5;padding:12px;background:#f7f9f8; }
.inline-comment-list { display:flex;flex-direction:column;gap:11px;margin-bottom:12px; }.inline-comment { display:flex;align-items:flex-start;gap:9px; }.comment-avatar { width:28px;height:28px;display:grid;flex:0 0 28px;place-items:center;border-radius:50%;background:#e7eefb;color:#3564b7;font-size:10px;font-weight:700; }
.inline-comment>div { min-width:0;flex:1; }.inline-comment header { display:flex;align-items:baseline;gap:8px; }.inline-comment header strong { color:#3f5f92;font-size:11px; }.inline-comment header small { color:#9aa49e;font-size:9px; }.inline-comment p { margin:4px 0 0;color:#4a564f;font-size:11px;line-height:1.65;white-space:pre-wrap;word-break:break-word; }
.inline-comment-state { padding:10px;color:#8b9690;font-size:10px;text-align:center; }.inline-reply-form { display:grid;grid-template-columns:1fr auto;gap:8px;align-items:end;border-top:1px solid #e1e7e3;padding-top:11px; }.inline-reply-form textarea { width:100%;min-height:54px;box-sizing:border-box;resize:vertical;border:1px solid #d8e0da;border-radius:5px;padding:8px 9px;background:#fff;color:#344139;font:inherit;font-size:11px;line-height:1.55; }.inline-reply-form textarea:focus { border-color:#7192d5;box-shadow:0 0 0 3px rgba(37,99,235,.08);outline:0; }.inline-reply-form>div { display:flex;align-items:flex-end;flex-direction:column;gap:6px; }.inline-reply-form>div>span { color:#9aa49e;font-size:8px; }.inline-reply-form button { min-width:68px;min-height:36px;border:0;border-radius:5px;padding:0 12px;background:#2563eb;color:#fff;font-size:10px;font-weight:700; }.inline-reply-form button:disabled { cursor:not-allowed;background:#c3cbc6; }
.community-image-viewer { min-height:260px;display:grid;place-items:center;position:relative;background:#111; }.preview-image { width:100%;max-height:72vh;display:block;object-fit:contain; }.image-nav { width:42px;height:52px;display:grid;place-items:center;position:absolute;z-index:2;top:50%;border:0;border-radius:5px;background:rgba(0,0,0,.5);color:#fff;font-size:22px;transform:translateY(-50%); }.image-nav-prev { left:12px; }.image-nav-next { right:12px; }.image-counter { position:absolute;right:12px;bottom:10px;border-radius:99px;padding:4px 9px;background:rgba(0,0,0,.58);color:#fff;font-size:10px; }
.detail-content { padding: 0 3px; }
.detail-author { display: flex; align-items: center; gap: 10px; padding-bottom: 14px; border-bottom: 1px solid #edf0ee; }
.detail-author .post-status { margin-left: auto; }
.detail-text { margin: 18px 0 0; color: #4b5750; font-size: 13px; line-height: 1.85; white-space: pre-wrap; word-break: break-word; }
.detail-images { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
.detail-images button { width: 128px; height: 90px; overflow: hidden; position: relative; border: 1px solid #e0e5e2; border-radius: 5px; padding: 0; background: #f1f4f2; cursor: pointer; }
.detail-images img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .18s ease, filter .18s ease; }
.detail-images button > i { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(19,33,25,.42); color: #fff; font-size: 18px; opacity: 0; transition: opacity .18s ease; }
.detail-images button:hover img { filter: brightness(.82); transform: scale(1.04); }
.detail-images button:hover > i, .detail-images button:focus-visible > i { opacity: 1; }
.detail-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 18px; border-top: 1px solid #edf0ee; padding-top: 12px; color: #7f8a83; font-size: 10px; }
.detail-footer-copy { min-width: 0; display: flex; flex-wrap: wrap; gap: 6px 16px; }
.detail-delete-button { min-height: 36px; flex: 0 0 auto; }
.community-state { min-height: 250px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 9px; border: 1px solid #e5ebe7; border-radius: 6px; background: #fff; color: #89948d; text-align: center; }
.community-state > i { color: #6c8bd1; font-size: 30px; }
.community-state strong { color: #526159; font-size: 13px; }
.community-state span { font-size: 11px; }
.community-state button { margin-top: 4px; border: 1px solid #cbd8ee; border-radius: 5px; padding: 7px 13px; background: #f7faff; color: #3564bd; font-size: 11px; }
.state-error > i, .state-error strong { color: #bc514b; }
.pagination-wrap { display: flex; justify-content: center; padding: 24px 0 8px; }
.dialog-form { padding-right: 2px; }
.dialog-intro { margin: -8px 0 18px; color: #89948d; font-size: 12px; }
.dialog-label { display: flex; align-items: center; gap: 7px; margin: 15px 0 7px; color: #4a584f; font-size: 12px; font-weight: 700; }
.dialog-label em { color: #c7534c; font-size: 10px; font-style: normal; font-weight: 500; }
.dialog-label span { margin-left: auto; color: #98a29c; font-size: 10px; font-weight: 500; }
.dialog-textarea { width: 100%; box-sizing: border-box; border: 1px solid #d8e0da; border-radius: 5px; padding: 10px 11px; background: #fbfcfb; color: #2c3931; font-size: 12px; transition: border-color .18s ease, box-shadow .18s ease; }
.dialog-textarea:focus { border-color: #6c8cda; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.08); outline: 0; }
.dialog-textarea { resize: vertical; min-height: 132px; line-height: 1.7; }
.dialog-error { display: block; margin-top: 5px; color: #bd4b45; font-size: 10px; }
.upload-label { margin-top: 17px; }
.upload-zone { min-height: 84px; display: flex; align-items: center; justify-content: center; gap: 10px; border: 1px dashed #cbd5ce; border-radius: 5px; padding: 10px; background: #f8faf8; color: #66736b; font-size: 11px; text-align: center; cursor: pointer; transition: border-color .18s ease, background .18s ease; }
.upload-zone:hover, .upload-zone.dragging { border-color: #7192df; background: #f2f6ff; }
.upload-zone.disabled { cursor: not-allowed; opacity: .6; }
.upload-zone input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.upload-zone > i { color: #5f83d6; font-size: 25px; }
.upload-zone span { display: flex; flex-direction: column; gap: 4px; }
.upload-zone small { color: #9aa49e; font-size: 10px; }
.attachment-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 7px; margin-top: 9px; }
.attachment-grid figure { height: 68px; overflow: hidden; position: relative; margin: 0; border: 1px solid #dfe6e1; border-radius: 5px; background: #f2f4f2; }
.attachment-grid img { width: 100%; height: 100%; display: block; object-fit: cover; }
.attachment-grid button { width: 22px; height: 22px; position: absolute; top: 3px; right: 3px; display: grid; place-items: center; border: 0; border-radius: 50%; background: rgba(27,37,31,.78); color: #fff; font-size: 10px; }
.dialog-submit-error { display: flex; align-items: center; gap: 6px; margin-top: 13px; border: 1px solid #efd1cf; border-radius: 5px; padding: 9px 10px; background: #fff6f5; color: #a6433f; font-size: 11px; }
.dialog-footer { display: flex; justify-content: flex-end; gap: 9px; }
.secondary-button { min-height: 38px; border: 1px solid #d9e1dc; border-radius: 5px; padding: 0 16px; background: #fff; color: #68766d; font-size: 12px; }
.secondary-button:hover:not(:disabled) { border-color: #adc0e5; color: #315fb9; }
.dialog-submit { min-height: 38px; box-shadow: none; }
@media (max-width: 767px) {
  .community-page { width: calc(100% - 24px); margin: 16px auto 40px; }
  .community-hero { align-items: flex-start; flex-direction: column; padding: 20px; }
  .hero-actions { width: 100%; }
  .hero-actions button { flex: 1; }
  .refresh-button, .community-state button, .secondary-button, .dialog-submit { min-height: 44px; }
  .community-toolbar > div { align-items: flex-start; flex-direction: column; gap: 4px; }
  .post-card { padding: 15px 13px 13px; }
  .post-author { align-items: flex-start; flex-wrap: wrap; }
  .post-actions { width: 100%; justify-content: space-between; margin-left: 48px; }
  .post-delete-button, .detail-delete-button, .detail-hint { min-height: 44px; }
  .post-summary { margin-left: 0; }
  .post-summary-text { font-size: 13px; }
  .feed-image-grid { width:100%;gap:4px; }.feed-image-grid.image-count-1,.feed-image-grid.image-count-2 { width:100%; }.summary-meta { gap: 10px; }
  .reply-toggle { min-height:44px; }.inline-discussion { margin-right:-13px;margin-left:-13px;padding:11px 13px; }.inline-reply-form { grid-template-columns:1fr; }.inline-reply-form textarea { min-height:72px;font-size:16px; }.inline-reply-form>div { align-items:center;flex-direction:row;justify-content:space-between; }.inline-reply-form button { min-width:82px;min-height:44px; }.image-nav { width:44px;height:52px; }
  .detail-hint { padding: 0 8px; }
  .detail-footer { align-items: stretch; flex-direction: column; }
  .detail-footer-copy { align-items: flex-start; flex-direction: column; }
  .detail-delete-button { width: 100%; }
  .detail-images button { width: 96px; height: 68px; }
  .attachment-grid { grid-template-columns: repeat(3, minmax(0,1fr)); }
}
@media (prefers-reduced-motion: reduce) {
  .primary-button, .secondary-hero-button, .detail-images img, .detail-images button > i, .post-card, .post-delete-button, .detail-delete-button, .dialog-textarea, .upload-zone { transition: none; }
}
</style>
